import dynamic, {DynamicOptions, Loader} from 'next/dynamic'

class ModuleLoadError extends Error {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(originalError: Error | any) {
    super(`${originalError?.message || originalError}`)
    this.name = 'ModuleLoadError'
    this.stack = originalError.stack
  }
}

// taken from https://dev.to/goenning/how-to-retry-when-react-lazy-fails-mb5
function retry<Exports extends object>(
  fn: () => Promise<Exports>,
  {retries = 3, interval = 500, exponentialBackoff = true} = {}
) {
  return new Promise<Exports>((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: Error) => {
        setTimeout(() => {
          if (retries === 1) {
            if (typeof Sentry !== 'undefined') {
              Sentry.captureException(new ModuleLoadError(error))
            }
            reject(error)
            return
          }

          // Passing on "reject" is the important part
          retry(fn, {
            retries: retries - 1,
            interval: exponentialBackoff ? interval * 2 : interval,
            exponentialBackoff
          }).then(resolve, reject)
        }, interval)
      })
  })
}

const dynamicWithOptions = <Props extends object>(
  loader: () => Loader<Props>,
  options: DynamicOptions = {}
) =>
  // @ts-expect-error - all the generic types don't play nice
  // eslint-disable-next-line @vio/custom-rules/enforce-dynamic-import-ssr
  dynamic<Props>(() => retry(loader), {
    // @ts-expect-error - next/dynamic type does not include timeout
    timeout: 1000,
    delay: 500,

    // Note: spreading options here causes compilation issues because it needs to be statically-analyzable
    // https://github.com/vercel/next.js/issues/32589#issuecomment-1016311047
    loading: options.loading || (() => null),
    ssr: !!options.ssr
  })

export default dynamicWithOptions
